import React, { useRef, useState } from "react";
import VocImageGrid from "./VocImageGrid";
import useFilePreviews from "./useFilePreviews";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faXmark, faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  ALLOWED_FILE_TYPES,
  MAX_ATTACHMENTS,
  MAX_FILE_BYTES,
} from "../../services/voc";

/**
 * ช่องแนบรูป — ลากมาวางหรือกดเลือกก็ได้ สูงสุด 3 รูป
 *
 * ตรวจชนิดและขนาดไฟล์ตั้งแต่ฝั่งเบราว์เซอร์ ผู้ใช้จะได้รู้ทันทีไม่ต้องรอส่ง
 * (ฝั่ง server ตรวจซ้ำอีกรอบอยู่แล้ว ตรงนี้แค่ช่วยให้ใช้ง่ายขึ้น)
 */
export default function VocUploader({ files, onChange, lang }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");

  const t = (th, en) => (lang === "th" ? th : en);
  const previews = useFilePreviews(files);

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []);
    if (list.length === 0) return;

    const room = MAX_ATTACHMENTS - files.length;
    if (room <= 0) {
      setError(t(`แนบได้สูงสุด ${MAX_ATTACHMENTS} รูป`, `Up to ${MAX_ATTACHMENTS} images`));
      return;
    }

    const accepted = [];
    for (const file of list.slice(0, room)) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setError(t("รองรับเฉพาะไฟล์ .jpeg .png .jpg", "Only .jpeg .png .jpg files"));
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        setError(t("ไฟล์ต้องมีขนาดไม่เกิน 20MB", "Each file must be under 20MB"));
        continue;
      }
      accepted.push(file);
    }

    if (accepted.length > 0) {
      setError("");
      onChange([...files, ...accepted]);
    }
  };

  const removeAt = (index) => {
    setError("");
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div>
      {files.length < MAX_ATTACHMENTS && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            addFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={
            `rounded-[8px] border-[1px] border-dashed px-[20px] py-[28px] text-center cursor-pointer transition-colors ` +
            (dragging
              ? "border-slateGray bg-lightGray"
              : "border-cardBorder bg-white hover:bg-lightGray3 active:bg-lightGray")
          }
        >
          <FontAwesomeIcon icon={faUpload} className="text-slateGray text-lead" />
          <p className="text-meta text-slateGray mt-[8px]">
            {t(
              "ลากไฟล์มาวางที่นี่หรือคลิกเพื่อเลือกไฟล์",
              "Drag files here or click to browse"
            )}
          </p>
          <p className="text-small text-metaGray mt-[4px]">
            {t(
              "รองรับไฟล์ .jpeg .png .jpg — ขนาดไม่เกิน 20MB",
              "Supports .jpeg .png .jpg — up to 20MB"
            )}
          </p>

          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ALLOWED_FILE_TYPES.join(",")}
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = ""; // เลือกไฟล์เดิมซ้ำได้
            }}
          />
        </div>
      )}

      {error && <p className="text-small text-brand mt-[8px]">{error}</p>}

      {files.length > 0 && (
        <VocImageGrid
          images={previews}
          className="grid grid-cols-3 gap-[12px] mt-[12px]"
          lang={lang}
          renderExtra={(i) => (
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label={t("ลบรูปนี้", "Remove image")}
              className="absolute top-[6px] right-[6px] w-[24px] h-[24px] rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
            >
              <FontAwesomeIcon icon={faXmark} className="text-small" />
            </button>
          )}
        >
          {files.length < MAX_ATTACHMENTS && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="rounded-[8px] border-[1px] border-dashed border-cardBorder bg-white aspect-square flex flex-col items-center justify-center text-slateGray hover:bg-lightGray3 active:bg-lightGray transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} className="text-lead" />
              <span className="text-small mt-[4px]">{t("เพิ่มรูป", "Add")}</span>
            </button>
          )}
        </VocImageGrid>
      )}
    </div>
  );
}
